import cropperjs from 'cropperjs';

import { AsyncResult, Action, ApiAction, Result } from '../../index';
import api from '../../redux-api';
import { SELECT_DELEGATE_SUCCESS } from '../delegates';
import { correlateApi } from '@/utils/api';
import localStorageUtils from '@/utils/local-storage';
import heapUtils from '@/utils/3rd-party/heap';
import zendeskUtils from '@/utils/3rd-party/zendesk';
import sessionStorage, { SessionStorageKey } from '@/utils/session-storage';
import { getInitialUserPreferences } from '@/utils/users';

// Types
import { AuthState, ResetTokenValidation } from '../types/auth';
import UserPreferences from '@/types/user-preferences';
import deepmerge from 'deepmerge';
import User from '@/types/user';
import EditableUserInfos from '@/types/editable-user-infos';
import DataProvider from '@/types/data-provider';
import ThemeMode from '@/types/theme-mode';

// Utils
import { isDevDebugMode } from '@/utils/environment';
import datadogUtils from '@/utils/3rd-party/datadogUtils';
import { NAV_PATHS } from '@/utils/constants';

// Initial state
const INITIAL_STATE: AuthState = {
  authenticated: false,
  loadingOAuth2: false,
  context: null,
};

// Actions
export const LOGIN: ApiAction = {
  type: 'connect/auth/login',
  endpoint: 'auth/login',
};

export const GENERATE_SSO_URL: ApiAction = {
  type: 'connect/auth/sso/generate',
  endpoint: 'auth/sso/generate',
};

const LOGOUT: ApiAction = {
  type: 'connect/auth/logout',
  endpoint: 'auth/logout',
};

const TIMEOUT: Action = {
  type: 'connect/auth/timeout',
};

const CLEAR_TIMEOUT: Action = {
  type: 'connect/auth/clear-timeout',
};

const GET_ME: ApiAction = {
  type: 'connect/auth/get-me',
  endpoint: 'auth/me',
};

const PATCH_ME: ApiAction = {
  type: 'connect/auth/patch-me',
  endpoint: 'auth/me',
};

const FORGOT_PASSWORD: ApiAction = {
  type: 'connect/auth/forgot-password',
  endpoint: 'auth/forgot-password',
};

const SET_PASSWORD: ApiAction = {
  type: 'connect/auth/set-password',
  endpoint: 'auth/set-password',
};

const UPDATE_PASSWORD: ApiAction = {
  type: 'connect/auth/update-password',
  endpoint: 'auth/update-password',
};

const GET_UPLOAD_SIGNED_REQUEST: ApiAction = {
  type: 'connect/auth/get-upload-signed-request',
  endpoint: 'uploads/img-url',
};

const CROP_USER_IMG: ApiAction = {
  type: 'connect/auth/crop-user-img',
  endpoint: 'users/img-crop',
};

const CROP_BRANDING_LOGO: ApiAction = {
  type: 'connect/auth/crop-branding-logo',
  endpoint: 'users/logo-crop',
};

const INVITE_PEOPLE: ApiAction = {
  type: 'connect/auth/invite-people',
  endpoint: 'users/invite',
};

const VALIDATE_USER: ApiAction = {
  type: 'connect/auth/validate-user',
  endpoint: 'auth/validate-user/:token/',
};

const PERSISTENCE_OAUTH2_AUTHORIZE: ApiAction = {
  type: 'connect/persistence/oauth2/authorize',
  endpoint: 'persistence-oauth2/authorize',
};

const PERSISTENCE_OAUTH2_PREPARE: Action = {
  type: 'connect/persistence/oauth2/prepare',
};

export const PERSISTENCE_OAUTH2_CLEAR: Action = {
  type: 'connect/persistence/oauth2/clear',
};

const VALIDATE_RESET_TOKEN: ApiAction = {
  type: 'connect/auth/validate-reset-token',
  endpoint: 'auth/validate-reset-token/:token/',
};

export const AUTHENTICATE_USER: Action = {
  type: 'connect/auth/user',
};

const PATCH_PREFERENCES: ApiAction = {
  type: 'connect/users/update-preferences',
  endpoint: 'users/update-preferences',
};

const SET_THEME: Action = {
  type: 'connect/users/set-theme',
};

const SET_DEV_PERMISSIONS: Action = {
  type: 'dev/users/set-dev-permissions',
};

// Action types
export const LOGOUT_SUCCESS = `${LOGOUT.type}_SUCCESS`;

// Reducer
export default function reducer(state: AuthState = INITIAL_STATE, action: Action): AuthState {
  switch (action.type) {
    case AUTHENTICATE_USER.type: {
      return {
        ...state,
        authenticated: true,
      };
    }
    case SELECT_DELEGATE_SUCCESS: {
      return {
        ...state,
        user: action.data.data,
      };
    }
    case `${LOGIN.type}_SUCCESS`:
    case `${PATCH_ME.type}_SUCCESS`:
    case `${SET_PASSWORD.type}_SUCCESS`:
      return {
        ...state,
        authenticated: true,
        user: action.data.data,
      };
    case `${GET_ME.type}_SUCCESS`:
      return {
        ...state,
        authenticated: true,
        user: {
          ...action.data.data,
          preferences: deepmerge({ comparisonSettings: getInitialUserPreferences(action.data.data) }, action.data.data.preferences || {}),
        },
        context: action.data.context,
      };
    case `${PERSISTENCE_OAUTH2_AUTHORIZE.type}`:
      return {
        ...state,
        loadingOAuth2: true,
      };
    case `${PERSISTENCE_OAUTH2_AUTHORIZE.type}_SUCCESS`:
      return {
        ...state,
        user: action.data.data,
        loadingOAuth2: false,
        oAuth2Provider: null,
        oAuthRedirect: null,
      };
    case LOGOUT_SUCCESS:
    case `${PERSISTENCE_OAUTH2_AUTHORIZE.type}_ERROR`:
    case `${GET_ME.type}_ERROR`:
      return {
        ...state,
        authenticated: false,
        user: null,
        loadingOAuth2: false,
        oAuth2Provider: null,
        oAuthRedirect: null,
        timeout: false,
        timeoutUrl: undefined,
      };
    case `${PERSISTENCE_OAUTH2_PREPARE.type}`:
      return {
        ...state,
        oAuth2Provider: action.options.provider,
        oAuthRedirect: action.options.from.pathname,
      };
    case `${PERSISTENCE_OAUTH2_CLEAR.type}`:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              oAuth2Providers: [],
            }
          : undefined,
      };
    case TIMEOUT.type:
      return {
        ...state,
        authenticated: false,
        user: null,
        loadingOAuth2: false,
        oAuth2Provider: null,
        oAuthRedirect: null,
        timeout: true,
        timeoutUrl: action.timeoutUrl,
      };
    case CLEAR_TIMEOUT.type:
      return {
        ...state,
        timeout: false,
      };
    case `${PATCH_PREFERENCES.type}_SUCCESS`: {
      if (action.payload.locale) {
        return {
          ...state,
          locale: action.payload.locale,
        };
      }
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              preferences: {
                ...state.user.preferences,
                comparisonSettings: {
                  ...getInitialUserPreferences(state.user),
                  ...state.user.preferences?.comparisonSettings,
                  ...action.payload.comparisonSettings,
                },
              },
            }
          : undefined,
      };
    }
    case SET_THEME.type:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              preferences: {
                ...state.user.preferences,
                theme: action.theme,
              },
            }
          : undefined,
      };
    case SET_DEV_PERMISSIONS.type:
      if (!isDevDebugMode()) {
        return state;
      }
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              permissions: [...action.permissions],
            }
          : undefined,
      };
    default:
      return state;
  }
}

// Action Creators
export const logIn =
  (email: string, password: string): AsyncResult<User> =>
  (dispatch, getState) =>
    api.post(LOGIN, {}, { email, password });

export const generateSSOUrl =
  (domain: string): AsyncResult<{ url: string }> =>
  (dispatch, getState) =>
    api.post(GENERATE_SSO_URL, {}, { enterpriseDomain: domain });

export const sessionTimeout = (): Result<void> => (dispatch, getState) => {
  const {
    auth: { user },
  } = getState();

  if (!user) {
    return Promise.resolve();
  }

  const timeoutUrl = sessionStorage.getItem(SessionStorageKey.TIMEOUT_URL);

  dispatch({ ...TIMEOUT, timeoutUrl });
  heapUtils.resetIdentity();
  datadogUtils.resetIdentity();
  localStorageUtils.clearProposalOptions();
  localStorageUtils.clearProposalV2Options();
  zendeskUtils.logout();
};

export const clearTimeout = (): Result<void> => dispatch => {
  dispatch({ ...CLEAR_TIMEOUT });
};

export const logOut = (): AsyncResult<string | null> => (dispatch, getState) => {
  const {
    auth: { user },
  } = getState();

  if (!user) {
    return Promise.resolve(null);
  }

  let logoutUrl = sessionStorage.getItem(SessionStorageKey.LOGOUT_URL);
  sessionStorage.clear();

  return api.post(LOGOUT).then((res: any) => {
    heapUtils.resetIdentity();
    datadogUtils.resetIdentity();
    localStorageUtils.clearProposalOptions();
    localStorageUtils.clearProposalV2Options();

    if (res?.path != null && !logoutUrl) {
      logoutUrl = res.path;
    }

    return logoutUrl || null;
  });
};

export const getMe =
  (contextId: string | undefined): AsyncResult<User> =>
  (dispatch, getState) => {
    const params: any = {};
    if (contextId != null) {
      params.contextId = contextId;
    }
    return api
      .get(GET_ME, params)
      .then((res: { data: any }) => res.data)
      .catch(err => {
        // Case where we want to redirect to error page
        const errorKey = err?.data?.errorKey;
        const error = err?.data?.error;
        if (errorKey === 'NOT_AUTHORIZED' && error?.type === 'ERROR_PAGE') {
          const encodedQueryParams = encodeURIComponent(error?.message);
          window.location.replace(`${NAV_PATHS.ERROR}?error=${encodedQueryParams}`);
        }
      });
  };

export const updateMe =
  (infos: EditableUserInfos): AsyncResult<User> =>
  (dispatch, getState) =>
    api.patch(PATCH_ME, null, infos).then((res: { data: any }) => res.data);

export const forgotPassword =
  (email: string): AsyncResult<void> =>
  (dispatch, getState) =>
    api.put(FORGOT_PASSWORD, {}, { email });

export const setPassword =
  (password: string, token: string, hash: string, fname?: string, lname?: string): AsyncResult<User> =>
  (dispatch, getState) =>
    api.put(SET_PASSWORD, {}, { password, token, hash, fname, lname }).then((res: { data: any }) => res.data);

export const updatePassword =
  (current: string, password: string, confirm: string): AsyncResult<void> =>
  (dispatch, getState) =>
    api.put(UPDATE_PASSWORD, {}, { currentPw: current, newPw: password, confirmPw: confirm });

export const uploadImg =
  (file: File): AsyncResult<string> =>
  (dispatch, getState) => {
    const query = { fileType: file.type, fileName: file.name };

    return api
      .get(GET_UPLOAD_SIGNED_REQUEST, query)
      .then((res: { url: string; signedRequest: string }) =>
        correlateApi.put(res.signedRequest, file, { withCredentials: false, headers: { 'Content-Type': file.type } }).then(() => res.url),
      );
  };

export const cropUserImg =
  (imgUrl: string, fileType: string, dimensions: cropperjs.Data): AsyncResult<string> =>
  (dispatch, getState) =>
    api.put(CROP_USER_IMG, null, { imgUrl, fileType, dimensions }).then((res: { jobId: string }) => res.jobId);

export const cropBrandingLogo =
  (imgUrl: string, fileType: string, dimensions: cropperjs.Data): AsyncResult<string> =>
  (dispatch, getState) =>
    api.put(CROP_BRANDING_LOGO, null, { imgUrl, fileType, dimensions }).then((res: { jobId: string }) => res.jobId);

export const inviteUsers =
  (emails: string[]): AsyncResult<void> =>
  (dispatch, getState) =>
    api.post(INVITE_PEOPLE, null, { emails });

export const validateUser =
  (token: string): AsyncResult<boolean> =>
  (dispatch, getState) =>
    api.get(VALIDATE_USER, { token }).then((res: { data: any }) => res.data.isOnboarded);

export const authorizeOAuth2 =
  (code: string, providerName: string): AsyncResult<void> =>
  (dispatch, getState) =>
    api.post(PERSISTENCE_OAUTH2_AUTHORIZE, null, { code, dataProviderName: providerName });

export const prepareOAuth2 =
  (provider: DataProvider, from: any): Result<void> =>
  (dispatch, getState) => {
    const options = { provider, from };
    dispatch({ type: PERSISTENCE_OAUTH2_PREPARE.type, options });
  };

export const validateResetToken =
  (token: string): AsyncResult<ResetTokenValidation> =>
  () =>
    api.get(VALIDATE_RESET_TOKEN, { token }).then((res: { data: any }) => res.data);

export const updatePreferences =
  (preferences: Partial<UserPreferences>): AsyncResult<void> =>
  async () =>
    api.patch(PATCH_PREFERENCES, {}, preferences).then((res: { data: any }) => res.data);

export const updateTheme =
  (theme: ThemeMode): Result<void> =>
  dispatch => {
    dispatch({ type: SET_THEME.type, theme });
  };

export const updateDevPermissions =
  (permissions: string[]): Result<void> =>
  dispatch => {
    dispatch({ type: SET_DEV_PERMISSIONS.type, permissions });
  };
